import type { ColumnDef } from '@tanstack/react-table';
import { Skeleton, createAppColumnHelper, type DataTableFeatures } from 'design-system';

import type { SymbolKey } from '@/entities/symbol';
import type {
  SymbolPreviewPresentation,
  SymbolPreviewSlots
} from '@/features/_shared/symbol-preview-registry';

export type SymbolPreviewTableRow = {
  id: string;
  symbol: SymbolKey;
  label?: string;
  alias: string;
  title: string;
  periodAbs: string;
  currency: string;
  units: string;
  loading: boolean;
};

type SymbolPreviewColumnOptions = {
  allSymbols: SymbolKey[];
  forChat: boolean;
  hideInfo: boolean;
  presentation: SymbolPreviewPresentation;
  slots: Readonly<SymbolPreviewSlots>;
};

const columnHelper = createAppColumnHelper<SymbolPreviewTableRow>();

function renderWhenLoaded(loading: boolean, content: React.ReactNode) {
  return loading ? null : content;
}

export function getSymbolPreviewColumns({
  allSymbols,
  forChat,
  hideInfo,
  presentation,
  slots
}: SymbolPreviewColumnOptions): ColumnDef<DataTableFeatures, SymbolPreviewTableRow>[] {
  const selectionColumn = columnHelper.display({
    id: 'selection',
    size: 90,
    header: () => (forChat ? slots.selectAll?.(allSymbols) : null),
    cell: ({ row }) =>
      renderWhenLoaded(
        row.original.loading,
        <div
          className={
            presentation === 'nested'
              ? 'flex items-center gap-1 text-foreground'
              : 'flex items-center gap-1'
          }
        >
          {slots.checkbox?.(row.original.symbol, presentation)}
          {row.original.label ? <span>{row.original.label}</span> : null}
        </div>
      )
  });

  const aliasColumn = columnHelper.accessor('alias', {
    header: 'Alias',
    size: 120,
    cell: ({ row }) =>
      row.original.loading ? (
        <output
          aria-label={`Loading ${row.original.symbol.Code}`}
          className='block w-full'
        >
          <Skeleton className='h-6 w-full' />
        </output>
      ) : (
        row.original.alias
      )
  });

  const titleColumn = columnHelper.accessor('title', {
    header: 'Title',
    size: 260,
    cell: ({ row }) => {
      if (row.original.loading) return null;
      if (hideInfo || !slots.titleWrapper) return row.original.title;
      return slots.titleWrapper(row.original.symbol.Code, row.original.title);
    }
  });

  const periodAbsColumn = columnHelper.accessor('periodAbs', {
    header: 'PeriodAbs',
    size: 120,
    cell: ({ row, getValue }) => renderWhenLoaded(row.original.loading, getValue())
  });

  const currencyColumn = columnHelper.accessor('currency', {
    header: 'Currency',
    size: 100,
    meta: { align: 'end' },
    cell: ({ row, getValue }) => renderWhenLoaded(row.original.loading, getValue())
  });

  const unitsColumn = columnHelper.accessor('units', {
    header: 'Units',
    size: 100,
    meta: { align: 'end' },
    cell: ({ row, getValue }) => renderWhenLoaded(row.original.loading, getValue())
  });

  const infoColumn = columnHelper.display({
    id: 'info',
    header: 'Info',
    size: 110,
    meta: { align: 'end' },
    cell: ({ row }) =>
      renderWhenLoaded(row.original.loading, slots.info?.(row.original.symbol.Code))
  });

  return columnHelper.columns([
    ...(hideInfo ? [] : [selectionColumn]),
    aliasColumn,
    titleColumn,
    ...(forChat ? [] : [periodAbsColumn]),
    currencyColumn,
    unitsColumn,
    ...(hideInfo ? [] : [infoColumn])
  ]);
}
